import { useEffect, useMemo } from 'react'
import { useExistingEnvironment, useExistingServers, useLatestSqlImage } from '@/hooks/useInfra'
import { lookupErrorMessage } from '@/services/infraLive'
import { effectiveServerMode, offersExistingServer, useInfrastructureWizard } from '@/stores/infrastructureWizard'
import type { ExistingSqlServer, SqlAuthType } from '@/types/infrastructure'
import { asSqlServer, dbServerDetails } from '@/utils/existingServers'
import { passwordProblem } from '@/utils/infraPaasFields'
import { resolveServerName } from '@/utils/infraValidation'
import { usesPoolTiers } from '@/utils/sizing'
import page from '@/pages/NewDeploymentPage.module.css'
import styles from './Infrastructure.module.css'
import { ExistingServerSelect } from './ExistingServerSelect'
import { DbVmSizeCard } from './DbVmSizeCard'
import { NetworkSelect } from './NetworkSelect'
import { OptionCard } from './OptionCard'
import { PaasForm } from './PaasForm'
import { PublicIpQuestion } from './PublicIpQuestion'
import { ReadOnlyInfoCard } from './ReadOnlyInfoCard'
import { ServerChoice } from './ServerChoice'
import { ServerNameCard } from './ServerNameCard'
import { SqlStorageEditor } from './SqlStorageEditor'
import { TextField } from './TextField'

const AUTH: { value: SqlAuthType; title: string; text: string }[] = [
  { value: 'windows', title: 'Windows authentication', text: 'Domain accounts only.' },
  { value: 'sql', title: 'SQL authentication', text: 'A SQL login and password.' },
  { value: 'mixed', title: 'Mixed mode', text: 'Windows accounts and a SQL login.' },
]

/**
 * The DB server: Existing (one of the database servers of the chosen environment, region and resource group) or Dedicated. On the
 * Domain level a dedicated one is a SQL server VM (generated name, the latest SQL image, VNet and subnet, whether it gets a public IP, storage,
 * authentication, collation, and its fixed size); on the AppCentral level it is a new Azure SQL server (PaaS), which has no public IP question. On the
 * AppCentral level an existing server's SQL instances are not fetched. Process Pro Global's is always Dedicated: no Existing option is offered for it.
 */
export function DbServerSection({ requiredIps }: { requiredIps?: number }) {
  const product = useInfrastructureWizard((s) => s.product)
  const level = useInfrastructureWizard((s) => s.level)
  const environment = useInfrastructureWizard((s) => s.environment)
  const region = useInfrastructureWizard((s) => s.region)
  const resourceGroup = useInfrastructureWizard((s) => s.resourceGroup)
  const db = useInfrastructureWizard((s) => s.dbServer)
  const rds = useInfrastructureWizard((s) => s.rdsServer)
  const setMode = useInfrastructureWizard((s) => s.setDbMode)
  const select = useInfrastructureWizard((s) => s.selectExistingDb)
  const patchSqlVm = useInfrastructureWizard((s) => s.patchDedicatedSqlVm)
  const patchAzureSql = useInfrastructureWizard((s) => s.patchDedicatedAzureSql)
  const keepServers = useInfrastructureWizard((s) => s.setExistingDbServers)
  const setPublicIp = useInfrastructureWizard((s) => s.setDbPublicIp)
  const customerNumber = useInfrastructureWizard((s) => s.customerNumber)
  const serverSequences = useInfrastructureWizard((s) => s.serverSequences)
  const manualSqlServerName = useInfrastructureWizard((s) => s.manualSqlServerName)
  const mode = effectiveServerMode(product, db.mode, 'db')
  const domain = level === 'domain'
  const where = { environmentId: environment?.id ?? '', regionId: region?.id ?? '', resourceGroupId: resourceGroup?.id ?? '' }

  // Domain: the existing environment lists the database servers with their instances. AppCentral: the servers only.
  const withInstances = useExistingEnvironment(product, where.environmentId, where.regionId, where.resourceGroupId, mode === 'existing' && domain)
  const withoutInstances = useExistingServers(product, where.environmentId, where.regionId, where.resourceGroupId, mode === 'existing' && !domain)
  const query = domain ? withInstances : withoutInstances
  const servers = useMemo<ExistingSqlServer[] | undefined>(
    () => (domain ? withInstances.data?.sqlServers : withoutInstances.data?.sqlServers.map(asSqlServer)),
    [domain, withInstances.data, withoutInstances.data],
  )
  useEffect(() => {
    if (servers) keepServers(servers)
  }, [servers, keepServers])

  const latest = useLatestSqlImage(mode === 'dedicated' && domain ? product : null)
  // The latest SQL image is fetched, shown read-only, and kept in the store for the payload.
  useEffect(() => {
    if (latest.data && db.sqlVm.image?.id !== latest.data.id) patchSqlVm({ image: { id: latest.data.id, name: latest.data.name } })
  }, [latest.data, db.sqlVm.image?.id, patchSqlVm])

  if (!product || !level) return null
  const azureName = resolveServerName({ product, environment, serverSequences, manualSqlServerName }, 'db', 'azure')
  const tags = [`product=${product.name}`, `environment=${environment?.label ?? ''}`, customerNumber ? `customer=${customerNumber}` : null].filter(Boolean).join('; ')
  // A dedicated RDS server's network is offered as the starting point for a dedicated DB server.
  const suggested = effectiveServerMode(product, rds.mode, 'rds') === 'dedicated' ? rds.network : null

  return (
    <section className={styles.section} aria-label="DB server">
      <h4 className={styles.sectionTitle}>DB server</h4>
      <ServerChoice
        server="DB server"
        name="db-server-mode"
        value={mode}
        onChange={setMode}
        dedicatedOnly={!offersExistingServer(product, 'db')}
        existingDescription="Use a database server that already exists in this resource group."
        dedicatedDescription={domain ? 'Create a new SQL server for this customer.' : 'Create a new Azure SQL server for this customer.'}
      />

      {mode === 'existing' && (
        <ExistingServerSelect
          id="db-existing"
          label="Existing database server"
          servers={servers}
          value={db.existing}
          onChange={select}
          isLoading={query.isLoading}
          isError={query.isError}
          errorMessage={lookupErrorMessage(query.error) ?? 'Could not load the database servers of this resource group.'}
          onRetry={() => void query.refetch()}
          emptyMessage={`No existing database server found in ${resourceGroup?.name ?? ''} / ${region?.label ?? ''}.`}
          details={(server) => dbServerDetails(server, domain)}
        />
      )}

      {mode === 'dedicated' && domain && (
        <>
          <ServerNameCard role="db" idPrefix="db-dedicated-name" />
          <ReadOnlyInfoCard
            title="Latest SQL image"
            loading={latest.isLoading}
            error={latest.isError ? 'Could not load the latest SQL image.' : null}
            onRetry={() => void latest.refetch()}
            items={[
              { label: 'Image', value: latest.data?.name ?? '' },
              { label: 'OS', value: latest.data?.osVersion ?? '' },
            ]}
            note="The SQL server is built from the latest image used for this product."
          />
          {/* A new SQL VM is being created, so show its fixed size, marked as a recommendation that is not confirmed. Read-only: it cannot be chosen here. */}
          <DbVmSizeCard requiredIps={requiredIps} />
          <NetworkSelect product={product} {...where} value={db.sqlVm.network} onChange={(network) => patchSqlVm({ network })} defaultValue={suggested} label="DB server network" idPrefix="db-dedicated-net" />
          {/* Only a SQL server VM can have a public IP (the Azure SQL (PaaS) server below is not a VM, so it is not asked), and only a product the
              Traverse pipeline builds is asked: it is the one that reads the answer. */}
          {usesPoolTiers(product) && <PublicIpQuestion name="db-public-ip" value={db.sqlVm.publicIp} onChange={setPublicIp} />}
          <SqlStorageEditor product={product} regionId={where.regionId} value={db.sqlVm.storage} onChange={(storage) => patchSqlVm({ storage })} idPrefix="db-dedicated" />
          <fieldset className={styles.cardGroup} style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
            <legend className={styles.legend}>SQL authentication</legend>
            {AUTH.map((option) => (
              <OptionCard key={option.value} name="db-dedicated-auth" value={option.value} checked={db.sqlVm.authType === option.value} onChange={(value) => patchSqlVm({ authType: value as SqlAuthType })} title={option.title} description={option.text} />
            ))}
          </fieldset>
          {db.sqlVm.authType !== 'windows' && (
            <div className={page.formGrid}>
              <TextField id="db-dedicated-user" label="SQL admin username" required value={db.sqlVm.adminUser} onChange={(adminUser) => patchSqlVm({ adminUser })} autoComplete="off" hint="Start with a letter; letters, digits and underscores." />
              <TextField
                id="db-dedicated-password"
                label="SQL admin password"
                required
                type="password"
                value={db.sqlVm.adminPassword}
                onChange={(adminPassword) => patchSqlVm({ adminPassword })}
                autoComplete="new-password"
                problem={db.sqlVm.adminPassword === '' ? null : passwordProblem(db.sqlVm.adminPassword, db.sqlVm.adminUser)}
                hint="At least 12 characters, with at least 3 of: upper case, lower case, digits, symbols."
              />
            </div>
          )}
          <div className={page.formGrid}>
            {/* The collation and the authentication above belong to the server's main instance, so they are always asked. */}
            <TextField id="db-dedicated-collation" label="Collation" required value={db.sqlVm.collation} onChange={(collation) => patchSqlVm({ collation })} />
          </div>
        </>
      )}

      {mode === 'dedicated' && !domain && (
        <>
          {/* Azure SQL (PaaS) is not sized: no DB VM size is shown for it. */}
          <ServerNameCard role="db" style="azure" title="New Azure SQL server name" idPrefix="db-dedicated-azure-name" />
          <ReadOnlyInfoCard
            title="Derived automatically"
            items={[
              { label: 'Resource group', value: resourceGroup?.name ?? '', mono: true },
              { label: 'Region', value: region?.label ?? '' },
              ...(db.azureSql.paas.networkAccess === 'private' && azureName.ok ? [{ label: 'Private endpoint', value: `pe-${azureName.name}`, mono: true }] : []),
              { label: 'Tags', value: tags },
            ]}
            note="You do not need to enter these."
          />
          <NetworkSelect product={product} {...where} value={db.azureSql.network} onChange={(network) => patchAzureSql({ network })} defaultValue={suggested} label="Azure SQL server network" idPrefix="db-dedicated-azure-net" />
          <PaasForm values={db.azureSql.paas} onChange={(paas) => patchAzureSql({ paas })} idPrefix="db-dedicated-paas" />
        </>
      )}
    </section>
  )
}
